<script setup lang="ts">
import { useRouter } from 'vue-router'

import ViewLayout from '@/views/layouts/ViewLayout.vue'
import { ElMessage } from 'element-plus'
import { onMounted, ref } from 'vue'

import ShipmentDialog from '@/components/rc/shipments/ShipmentDialog.vue'
import ShipmentsTable from '@/components/rc/tables/ShipmentsTable.vue'
import { getAllCarriers, getShipmentDetails } from '@/services/rc/shipments.service'
import type { IDashboardShipment, IShipment } from '@/types/rc/shipments'

export interface ITableRow {
  id: number
}

const carriersOptions = ref<string[]>([])

const router = useRouter()

const createShipmentDialogOpen = ref<boolean>(false)

const editShipmentTarget = ref<Partial<IShipment>>()

const handleOpenShipment = (row: ITableRow) => {
  router.push({ name: 'rc-logistics-shipment-details', params: { shipmentId: row.id.toString() } })
}

onMounted(async () => {
  const data = await getAllCarriers()

  carriersOptions.value.splice(0, carriersOptions.value.length, ...data)
})

/**
 * Handles shipment edit button click, refreshes and sets edit target.
 */
const handleEdit = async (shipment: IDashboardShipment) => {
  try {
    const shipmentDetails = await getShipmentDetails(shipment.id)

    const {
      id,
      work_order,
      carrier,
      tracking_number,
      bol_number,
      waybill_number,
      expected_container_types,
      expected_containers,
      expected_miners,
      type,
      delivered_date,
      picked_up_date,
      customer_drop_off,
      customer_pick_up,
      receipts,
    } = shipmentDetails

    const commonShipmentDetails = {
      id,
      work_order,
      tracking_information: {
        carrier: carrier ?? undefined,
        tracking_number: tracking_number ?? '',
        bol_number: bol_number ?? '',
        waybill_number: waybill_number ?? '',
      },
      expected_container_types: expected_container_types ?? [],
      expected_containers: expected_containers ?? '',
      expected_miners: expected_miners ?? '',
      picked_up_date: picked_up_date ?? '',
      delivered_date: delivered_date ?? '',
      type,
      receipts: receipts ?? [],
    }

    if (type === 'Inbound') {
      editShipmentTarget.value = {
        ...commonShipmentDetails,
        customer_drop_off: customer_drop_off,
      }
    } else if (type === 'Outbound') {
      editShipmentTarget.value = {
        ...commonShipmentDetails,
        customer_pick_up: customer_pick_up,
      }
    }

    createShipmentDialogOpen.value = true
  } catch {
    ElMessage.error({
      message: 'Failed to refresh record prior to update, aborting.',
    })
  }
}
</script>

<template>
  <ViewLayout>
    <template #controls>
      <el-button type="primary" @click="createShipmentDialogOpen = true">Add shipment</el-button>
    </template>
    <ShipmentsTable :carriers-options="carriersOptions" @open-shipment="handleOpenShipment" @handle-edit="handleEdit" />
  </ViewLayout>
  <ShipmentDialog
    v-model:open="createShipmentDialogOpen"
    :shipment="editShipmentTarget"
    :carriers-options="carriersOptions"
    :kind="editShipmentTarget?.type"
    @closed="editShipmentTarget = undefined"
  />
</template>
